import { ChangeDetectionStrategy, inject, Component, Inject, OnChanges, SimpleChanges, input, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { ConsultorioHistoricoFeatureService } from './consultorio-historico.service';
import { ConsultorioHistoricoRequest } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-historico.model';
import { finalize } from 'rxjs/operators';
import { ActionChildComponent } from '../../../../../../../shared/components/containers/action-child.component';
import { BotaoAcoesType } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { MenuItem } from 'primeng/api';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';

@Component({
    selector: 'app-consultorio-historico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CardModule, ButtonModule, TooltipModule, CustomTableComponent],
    templateUrl: './consultorio-historico.html',
    styleUrls: ['./consultorio-historico.scss']
})
/** Aba de histórico do consultório privado. */
export class ConsultorioHistoricoComponent extends ActionChildComponent {
    private readonly logger = inject(LoggerService);
    constructor(
        @Inject(ConsultorioHistoricoFeatureService)
        private feature: ConsultorioHistoricoFeatureService
    ) {
        super();
    }

    // Pessoa de contexto
    pessoa = input<Pessoa>();
    // Consultório selecionado
    idConsultorio = input<number | null>(null);

    // Estrutura da listagem
    registros = signal<{ dataReferencia: string; situacao: string }[]>([]);

    // Estados
    loading = signal(false);
    erro = signal<string | undefined>(undefined);

    tableMetadata: TableMetadata = {
        headers: {
            dataReferencia: { type: 'text', value: 'Data Referência', hidden: false, default: '' },
            situacao: { type: 'text', value: 'Situação', hidden: false, default: '' }
        },
        title: 'situacao',
        actions: {
            remover: {
                label: '',
                icon: 'pi pi-trash',
                color: 'danger',
                command: (item: unknown) => this.deletar(item)
            }
        }
    };

    actions: BotaoAcoesType & { useEdit: boolean } = {
        items: [
            {
                label: 'Adicionar',
                icon: 'pi pi-plus-circle',
                command: () => this.novo(),
                styleClass: 'bg-green-500'
            }
        ],
        useEdit: false
    };

    override initComponent(): void {}
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa'] || changes['idConsultorio']) {
            const p = this.pessoa();
            const idC = this.idConsultorio();
            if (p && p.id !== undefined && p.id !== null && !!p.uf && typeof idC === 'number') {
                this.carregar({ id: p.id, uf: p.uf, idConsultorio: idC });
            } else {
                this.registros.set([]);
            }
        }
    }

    private carregar(params: Pessoa & { idConsultorio: number }): void {
        this.loading.set(true);
        this.erro.set(undefined);
        this.feature
            .buscar(params)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (lista: ConsultorioHistoricoRequest[] | null | undefined) => {
                    this.registros.set(
                        (lista ?? []).map((h: ConsultorioHistoricoRequest) => ({
                            dataReferencia: this.formatarData(h.dtSituacao),
                            situacao: h.dsTipoSituacao ?? h.inSituacao ?? ''
                        }))
                    );
                },
                error: (err: unknown) => {
                    this.logger.error('Erro ao buscar histórico do consultório', err);
                    this.registros.set([]);
                    this.erro.set('Falha ao carregar histórico do consultório.');
                }
            });
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            if (isNaN(d.getTime())) return '';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return '';
        }
    }

    override getDefinedActions(): BotaoAcoesType & { useEdit: boolean } {
        return this.actions;
    }

    novo() {
        this.logger.info('NOVO');
    }
    deletar(item: unknown) {
        this.logger.info('DELETAR', item);
    }

    override updateActionsWithIndexes(actions: [MenuItem, number][]) {}
    override toggleAction(action: MenuItem): MenuItem {
        return action;
    }
}
